import { useState, useCallback } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ChevronsUpDown, Check } from 'lucide-react';
import { useVirtualAccounts } from '@/modules/wallets/hooks/useVirtualAccounts';
import { cn } from '@/utils/classnames';
import { CustomIcon } from '@/components/CustomIcon';
import { CashbackCard } from './cashback-card';
import { useRootSelector } from '@/hooks/selector.hook';
import { useCashback } from '@/services/cashback.service';

type CashbackRedeemTabProps = {
    closeModal: () => void;
};

export const CashbackRedeemTab = ({ closeModal }: CashbackRedeemTabProps) => {
    const [selectedVirtualAccount, setSelectedVirtualAccount] = useState<{ value: string; label: string } | null>(null);
    const [isVirtualAccountOpen, setIsVirtualAccountOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [amount, setAmount] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { withdrawCashback } = useCashback();

    // Use the virtual accounts hook to fetch data from API
    const { visibleVirtualAccounts, isLoading, handleSearch, loadMore, hasMore } =
        useVirtualAccounts({ enabled: true });

    // Get cashback data from Redux
    const dataCashback = useRootSelector((state) => state.cashback.dataCashback);
    const tiers = dataCashback?.config?.tiers ?? [];
    const currentTier = dataCashback?.metrics?.currentTier;

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        }).format(amount).replace('$', '');
    };

    // Format volume for display
    const formatVolume = (min: number, max: number | null) => {
        if (max === null) {
            return `$${formatCurrency(min)}+ spend monthly`;
        }
        return `$${formatCurrency(min)}-$${formatCurrency(max)} spend monthly`;
    };

    // Ref for infinite scroll
    const lastElementRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (isLoading) return;
            if (node) {
                const observer = new IntersectionObserver((entries) => {
                    if (entries[0].isIntersecting && hasMore) {
                        loadMore();
                    }
                });
                observer.observe(node);
                return () => observer.disconnect();
            }
        },
        [isLoading, hasMore, loadMore]
    );

    const handleWithdraw = async () => {
        if (!amount || !selectedVirtualAccount?.value) return;
        setIsSubmitting(true);
        const idempotencyKey = crypto.randomUUID();
        const response = await withdrawCashback(Number(amount), selectedVirtualAccount.value, idempotencyKey);
        setIsSubmitting(false);
        if (response?.isSuccess) {
            closeModal();
        }
    };

    return (
        <div>
            <CashbackCard />

            <Accordion type="single" collapsible defaultValue="item-1">
                <AccordionItem value="item-1">
                    <div className='mt-[2.4rem] text-[#121212] dark:text-[#CDCDCD] px-[1.5rem] border border-[#CECECE] dark:border-[#717171] rounded-[0.8rem]'>
                        <div className='flex items-center gap-[1rem]'>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.888889 10.6667C1.3798 10.6667 1.77778 11.0646 1.77778 11.5556V14.2222H4.44444C4.93536 14.2222 5.33333 14.6202 5.33333 15.1111C5.33333 15.602 4.93536 16 4.44444 16H1.77778C0.795938 16 0 15.2041 0 14.2222V11.5556C0 11.0646 0.397973 10.6667 0.888889 10.6667ZM15.1111 10.6667C15.602 10.6667 16 11.0646 16 11.5556V14.2222C16 15.2041 15.2041 16 14.2222 16H11.5556C11.0646 16 10.6667 15.602 10.6667 15.1111C10.6667 14.6202 11.0646 14.2222 11.5556 14.2222H14.2222V11.5556C14.2222 11.0646 14.6202 10.6667 15.1111 10.6667ZM8.02169 1.77778C8.45129 1.77778 8.79947 2.126 8.79947 2.55556V2.88889C9.33271 2.93332 9.81822 3.14789 10.2552 3.53299C10.4688 3.72123 10.6496 3.92575 10.7978 4.14583C11.0585 4.53347 10.8217 5.02468 10.3889 5.20052C10.0322 5.34524 9.63547 5.14782 9.39067 4.85069C9.32 4.765 9.24142 4.68524 9.15538 4.61111C8.88871 4.38151 8.51822 4.26652 8.04427 4.26649C7.52578 4.26649 7.12907 4.3776 6.85502 4.59982C6.58098 4.82204 6.44444 5.12611 6.44444 5.51129C6.44453 5.89624 6.6144 6.19971 6.95484 6.42187C7.29556 6.64409 7.91076 6.90338 8.79947 7.19964C9.86613 7.5848 10.5776 8.03707 10.9332 8.55556C11.2886 9.07404 11.4661 9.64436 11.4661 10.2665C11.4661 10.6961 11.3921 11.0742 11.2439 11.4002C11.0958 11.726 10.8997 12.0039 10.6554 12.2335C10.4109 12.4631 10.1254 12.6484 9.79947 12.7891C9.4736 12.9298 9.12551 13.0371 8.7552 13.1111V13.4444C8.7552 13.874 8.40702 14.2222 7.97742 14.2222C7.54791 14.2222 7.19964 13.874 7.19964 13.4444V13.0668C6.50347 12.9039 5.93701 12.6036 5.5 12.1667C5.23441 11.9011 5.01065 11.5847 4.82986 11.2179C4.62234 10.7964 4.86767 10.313 5.30468 10.1406C5.67772 9.99369 6.08663 10.2058 6.27956 10.5573C6.43076 10.833 6.61573 11.0586 6.83333 11.2335C7.21102 11.537 7.64409 11.6892 8.1328 11.6892C8.62169 11.6892 9.04071 11.5742 9.38889 11.3446C9.73689 11.115 9.91058 10.7553 9.91058 10.2665C9.91058 9.83698 9.72924 9.48889 9.36631 9.22222C9.00338 8.95556 8.3552 8.65147 7.42187 8.31076C6.54782 7.99964 5.90739 7.62924 5.5 7.19964C5.09263 6.77004 4.88892 6.20749 4.88889 5.51129C4.88889 4.90399 5.09964 4.35212 5.5217 3.8559C5.94389 3.35964 6.51804 3.03705 7.24391 2.88889V2.55556C7.24391 2.12606 7.59227 1.77788 8.02169 1.77778ZM4.44444 0C4.93536 0 5.33333 0.397973 5.33333 0.888889C5.33333 1.3798 4.93536 1.77778 4.44444 1.77778H1.77778V4.44444C1.77778 4.93536 1.3798 5.33333 0.888889 5.33333C0.397973 5.33333 0 4.93536 0 4.44444V1.77778C0 0.795938 0.795938 0 1.77778 0H4.44444ZM14.2222 0C15.2041 0 16 0.795938 16 1.77778V4.44444C16 4.93536 15.602 5.33333 15.1111 5.33333C14.6202 5.33333 14.2222 4.93536 14.2222 4.44444V1.77778H11.5556C11.0646 1.77778 10.6667 1.3798 10.6667 0.888889C10.6667 0.397973 11.0646 0 11.5556 0H14.2222Z" fill="currentColor" />
                            </svg>

                            <p className='font-medium text-[1.3rem] leading-[19.43px]  '>Your rate is based in your monthly spendings</p>

                            <div className='ml-auto'>
                                <AccordionTrigger className='font-medium text-[1.3rem] leading-[19.43px] text-[#57E8B7] '>View More</AccordionTrigger>
                            </div>
                        </div>
                    </div>
                    <AccordionContent>
                        <div className='py-[1.5rem] mt-[1.5rem] px-[2rem] flex flex-col gap-[1.5rem] border border-[#CECECE] dark:border-[#717171] rounded-[0.8rem]'>
                            {tiers.map((tier, index) => {
                                const isCurrentTier = tier.tierName === currentTier?.tierName;
                                const currentTierIndex = tiers.findIndex(t => t.tierName === currentTier?.tierName);
                                const isAchieved = index <= currentTierIndex;
                                const isNotFirst = index > 0;

                                return (
                                    <div key={tier.tierName} className='flex items-center gap-[1rem]'>
                                        <div className={cn(
                                            'relative w-[1rem] h-[1rem] rounded-full border-[0.2rem]',
                                            isAchieved
                                                ? isCurrentTier
                                                    ? 'border-[#4FD3A9] bg-[#4FD3A9] dark:border-[#57E8BA] dark:bg-[#57E8BA]'
                                                    : 'border-[#4FD3A9] bg-[#E0EEE9] dark:border-[#57E8BA] dark:bg-[#19382E]'
                                                : 'border-[#CDCDCD] bg-[#E0EEE9] dark:border-[#4E4E4E] dark:bg-[#19382E]'
                                        )}>
                                            {isNotFirst && (
                                                <div className={cn(
                                                    'absolute bottom-[100%] left-[50%] translate-x-[-50%] w-[0.3rem] h-[2.7rem]',
                                                    isAchieved
                                                        ? 'bg-[#4FD3A9] dark:bg-[#57E8BA]'
                                                        : 'bg-[#CDCDCD] dark:bg-[#4E4E4E]'
                                                )} />
                                            )}
                                        </div>
                                        <div className='flex items-center gap-[1rem] font-normal text-[1.2rem] leading-[19.43px]'>
                                            <p className={cn(
                                                isAchieved
                                                    ? 'text-[#121212] dark:text-[#F6F6F6]'
                                                    : 'text-[#929292] dark:text-[#929292]'
                                            )}>
                                                {tier.rate}%
                                            </p>
                                            <div className={cn(
                                                'w-[0.3rem] h-[0.3rem] rounded-full',
                                                isAchieved
                                                    ? 'bg-[#4FD3A9] dark:bg-[#57E8BA]'
                                                    : 'bg-[#CDCDCD] dark:bg-[#4E4E4E]'
                                            )} />
                                            <p className={cn(
                                                isAchieved
                                                    ? 'text-[#121212] dark:text-[#F6F6F6]'
                                                    : 'text-[#929292] dark:text-[#929292]'
                                            )}>
                                                {formatVolume(tier.minimumVolume, tier.maximumVolume)}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            <div className='flex items-center gap-[1rem] w-full mt-[2.4rem]'>
                <p className="text-[1.6rem] dark:text-[#DEDEDE] min-w-[5rem]">From</p>

                <div className='dark:bg-[#000000] flex-1 border dark:border-[#717171] border-[#CECECE] rounded-[0.8rem] p-[1rem] text-[1.3rem] dark:text-[#717171]'>
                    Cashback Balance
                </div>
            </div>

            <div className='flex items-center gap-[1rem] w-full mt-[2.4rem]'>
                <p className="text-[1.6rem] dark:text-[#DEDEDE] min-w-[5rem]">To</p>

                <div className='flex-1'>
                    <DropdownMenu open={isVirtualAccountOpen} onOpenChange={setIsVirtualAccountOpen}>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                align="between"
                                aria-expanded={isVirtualAccountOpen}
                                className="w-full justify-between text-[1.3rem] font-normal h-auto dark:bg-[#000000] bg-[#FFFFFF] dark:text-[#DEDEDE] text-[#121212] border dark:border-[#717171] border-[#CECECE] rounded-[0.8rem] p-[1rem]"
                            >
                                {selectedVirtualAccount?.label ?? "Select virtual account..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] p-0 rounded-[0.8rem] dark:border-[#2f2f2f] border-[#CECECE]" align="start">
                            <Command shouldFilter={false} className="dark:bg-[#101010] bg-[#FFFFFF] rounded-[0.8rem]">
                                <CommandInput
                                    placeholder="Search virtual account..."
                                    className="text-[1.3rem] placeholder:text-[#808081]"
                                    value={searchTerm}
                                    onValueChange={(value) => {
                                        setSearchTerm(value);
                                        handleSearch(value);
                                    }}
                                />
                                <CommandList>
                                    <CommandEmpty>No virtual account found.</CommandEmpty>
                                    <CommandGroup>
                                        {visibleVirtualAccounts.map((virtualAccount, index) => {
                                            const isLastElement = index === visibleVirtualAccounts.length - 1;
                                            return (
                                                <CommandItem
                                                    key={virtualAccount.value}
                                                    value={virtualAccount.value}
                                                    ref={isLastElement ? lastElementRef : null}
                                                    onSelect={() => {
                                                        setSelectedVirtualAccount({ value: virtualAccount.value, label: virtualAccount.label });
                                                        setIsVirtualAccountOpen(false);
                                                    }}
                                                    className="text-[1.4rem] rounded-[0.5rem] cursor-pointer dark:hover:bg-[#1A1A1A] hover:bg-[#F6F6F6]"
                                                >
                                                    <div className="flex items-center gap-[0.8rem] flex-1">
                                                        <div className="flex-1 flex gap-[0.8rem]">
                                                            <CustomIcon type="icon-virtualAccount" />
                                                            <div className='flex flex-col'>

                                                                <p className="text-[1.3rem] dark:text-[#DEDEDE] text-[#121212] font-medium">
                                                                    {virtualAccount.label}
                                                                </p>
                                                                <p className="text-[1.1rem] dark:text-[#929292] text-[#717171]">
                                                                    {virtualAccount.accountNumber}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <Check
                                                            className={cn(
                                                                "h-4 w-4",
                                                                selectedVirtualAccount?.value === virtualAccount.value
                                                                    ? "opacity-100 text-[#57E8BA]"
                                                                    : "opacity-0"
                                                            )}
                                                        />
                                                    </div>
                                                </CommandItem>
                                            );
                                        })}
                                        {isLoading && (
                                            <div className="px-[0.8rem] py-[0.4rem]">
                                                <div className="animate-pulse">
                                                    <div className="h-[4rem] dark:bg-[#2A2A2A] bg-[#E0E0E0] rounded-[0.5rem]"></div>
                                                </div>
                                            </div>
                                        )}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className='flex items-center gap-[1rem] w-full mt-[2.4rem]'>
                <p className="text-[1.6rem] dark:text-[#DEDEDE] min-w-[5rem]">Amount</p>

                <div className='flex-1'>
                    <input
                        type="number"
                        placeholder="Enter amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className='w-full dark:bg-[#000000] bg-[#FFFFFF] border dark:border-[#717171] border-[#CECECE] rounded-[0.8rem] p-[1rem] text-[1.3rem] dark:text-[#DEDEDE] text-[#121212] placeholder:dark:text-[#717171] placeholder:text-[#BCBCBC] focus:outline-none focus:ring-1 focus:ring-[#57E8BA]'
                    />
                </div>
            </div>

            {/* Action Buttons */}
            <div className='flex items-center gap-[1.2rem] justify-end w-full mt-[3rem]'>
                <Button
                    type="button"
                    variant="outline"
                    onClick={closeModal}
                    className='px-[2.4rem] py-[1rem] text-[1.4rem] font-medium rounded-[0.8rem] border dark:border-[#717171] border-[#CECECE] dark:text-[#DEDEDE] text-[#121212] hover:dark:bg-[#1A1A1A] hover:bg-[#F0F0F0] transition-colors'
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    variant="default"
                    onClick={handleWithdraw}
                    disabled={isSubmitting || !amount || !selectedVirtualAccount?.value}
                    className='px-[2.4rem] py-[1rem] text-[1.4rem] font-medium rounded-[0.8rem] bg-[#57E8BA] text-[#161616] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed'
                >
                    {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
            </div>
        </div>
    );
};
